import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { SecurityHolding } from "@models/shares/security-holding";
import { SecurityRegistryRecord } from "@models/shares/security-registry-record";
import { NgbDateStruct } from "@ng-bootstrap/ng-bootstrap";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { DatePickerComponent } from "@shared/controls/date-picker/date-picker.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { DatePickerFormValidators, DatepickerHelper } from "@shared/validators/date-picker-form-validators";
import { NumbersValidators } from "@shared/validators/numbers.validators";
import {
  ShareSecurityHoldingsDropdownComponent
} from "../../../controls/share-security-holdings-dropdown/share-security-holdings-dropdown.component";
import { CompanySecurityConsolidationSubdivision } from "../../../models/shares";
import {
  ReusableFormGroupComponent
} from "../../../reusable-form-groups/reusable-form-group/reusable-form-group.component";

export interface IShareSubdivisionConversionBlockFormControls {
  subdivisionDate: FormControl<Date | null>;
  securityHoldingClass: FormControl<string | null>;
  newNumberShares: FormControl<number | null>;
}

export type IShareSubdivisionConversionBlockForm = FormGroup<IShareSubdivisionConversionBlockFormControls>;

@Component({
  selector: 'app-share-subdivision-conversion-block',
  standalone: true,
  imports: [
    ShareSecurityHoldingsDropdownComponent,
    InputNumberComponent,
    ReactiveFormsModule,
    DatePickerComponent,
    DisclaimerComponent
  ],
  templateUrl: 'company-security-consolidation-subdivision-block.component.html',
})
export class CompanySecurityConsolidationSubdivisionBlockComponent extends ReusableFormGroupComponent<IShareSubdivisionConversionBlockForm> implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() customDatepickerErrors: Record<string, string> = DatePickerFormValidators.errorMessagesWithDateOfEstablishmentErrorMessages;
  @Input() minDate!: NgbDateStruct;
  @Input() maxDate!: NgbDateStruct;

  @Input('securityRegistryRecords') set securityRegistryRecordsHoldingsSetter(value: SecurityRegistryRecord[]) {
    this.fillSecurityRegistryRecordsHoldings(value);
  }

  readonly newNumberOfSharesCustomErrors = { mustBeNotEqual: 'Please use a number that is either larger or smaller.' };
  readonly newNumberOfSharesMinValue = -1000000000;

  securityRegistryRecordsHoldings: SecurityHolding[] = [];
  selectedSecurityHoldingClass: SecurityHolding | null = null;

  override form = CompanySecurityConsolidationSubdivisionBlockComponent.defineForm();

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.minDate) {
      this.minDate = DatepickerHelper.getTodayStruct();
    }

    if (!this.maxDate) {
      this.maxDate = DatepickerHelper.getNextNYearsStruct(10);
    }

    this.setupValueChangeListeners();
  }

  setupValueChangeListeners(): void {
    this.listenSecurityHoldingClassChange();
  }

  setupChange(change: CompanySecurityConsolidationSubdivision): void {
    const [securityHoldingClass, newNumberShares] = Array.from(Object.entries(change.modifiedClasses))[0];

    this.form.setValue({
      subdivisionDate: change.changeDate,
      securityHoldingClass: securityHoldingClass as string ?? null,
      newNumberShares: newNumberShares as number ?? 0,
    });
  }

  public buildShareFormChange(): CompanySecurityConsolidationSubdivision {
    const changeDate = DatepickerHelper.buildDateString(this.form.controls.subdivisionDate.value!);
    const classKey = this.form.controls.securityHoldingClass.value!;

    return new CompanySecurityConsolidationSubdivision({
      ...this.form.value as Partial<CompanySecurityConsolidationSubdivision>,
      changeDate,
      dateOfResolution: changeDate,
      modifiedClasses: {
        [classKey]: this.form.value.newNumberShares!
      }
    });
  }

  // Validators

  private setupShareNumberValidator(numberOfShares: number): void {
    const mustBeNotEqualValidator = (numberToBeNotEqual: number): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
      return control.value !== numberToBeNotEqual ? null : { mustBeNotEqual: control.value as number };
    };

    this.form.controls.newNumberShares.setValidators([
      Validators.required,
      NumbersValidators.isInteger,
      mustBeNotEqualValidator(numberOfShares)
    ]);
  }

  // Value Change Listeners

  private listenSecurityHoldingClassChange(): void {
    this.form.controls.securityHoldingClass.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((securityHoldingClass) => {
        const selectedSecurityHoldingClass = this.securityRegistryRecordsHoldings.find(holding => holding.securityHoldingId === securityHoldingClass);
        this.selectedSecurityHoldingClass = selectedSecurityHoldingClass ?? null;

        if (selectedSecurityHoldingClass) {
          this.setupShareNumberValidator(selectedSecurityHoldingClass.number);
        }
      });
  }

  private fillSecurityRegistryRecordsHoldings(securityRegistryRecords: SecurityRegistryRecord[]): void {
    const holdingsMap = new Map<string, SecurityHolding[]>();

    securityRegistryRecords
      .flatMap(record => record.holdings)
      .forEach(holding => {
        const securityTypeClass = holding.securityType.class;

        if (!holdingsMap.has(securityTypeClass)) {
          holdingsMap.set(securityTypeClass, [holding]);
        } else {
          holdingsMap.get(securityTypeClass)?.push(holding);
        }
      });

    this.securityRegistryRecordsHoldings = Array.from(holdingsMap.values())
      .map(holdings => {
        return new SecurityHolding({
          number: holdings.reduce((sum, holding) => sum + holding.number, 0),
          paid: holdings.reduce((sum, holding) => sum + holding.paid, 0),
          unpaid: holdings.reduce((sum, holding) => sum + holding.unpaid, 0),
          securityTypeId: holdings[0].securityType.securityTypeId,
          securityType: holdings[0].securityType,
          securityHoldingId: holdings[0].securityType.class
        });
      });

    const selectedSecurityHoldingClass = this.form.controls.securityHoldingClass.value;
    const selectedHolding = this.securityRegistryRecordsHoldings.find((holding) => holding.securityType.class == selectedSecurityHoldingClass);

    if (selectedSecurityHoldingClass && selectedHolding) {
      this.form.controls.securityHoldingClass.setValue(selectedHolding.securityHoldingId);
      this.selectedSecurityHoldingClass = selectedHolding;
    } else {
      this.form.controls.securityHoldingClass.setValue(null);
      this.selectedSecurityHoldingClass = null;
    }
  }

  static override defineFormControls(): IShareSubdivisionConversionBlockFormControls {
    return {
      subdivisionDate: new FormControl<Date | null>(null, [Validators.required]),
      securityHoldingClass: new FormControl<string | null>(null, [Validators.required]),
      newNumberShares: new FormControl<number | null>(null, [Validators.required, NumbersValidators.isInteger]),
    };
  }

  static override defineForm(): IShareSubdivisionConversionBlockForm {
    return new FormGroup<IShareSubdivisionConversionBlockFormControls>(CompanySecurityConsolidationSubdivisionBlockComponent.defineFormControls());
  }
}
